import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { S_POS } from "@/hooks/useGame";

const RING_COLOR = 0xf2c85b;
const COUNT = 9;

// Concentric acoustic sound rings radiating from Stanley's origin (S_POS).
export default function SoundRings({ game }) {
  const camera = useThree((s) => s.camera);
  const meshRefs = useRef([]);
  const ringT = useRef(new Array(COUNT).fill(2)); // >=1 means idle
  const pulseRef = useRef();

  const ringGeo = useMemo(() => new THREE.RingGeometry(0.9, 1.0, 48), []);
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: RING_COLOR,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    []
  );

  useFrame((state, dt) => {
    const g = game.current;
    // consume emit requests
    while (g._emitAck < g._emitReq) {
      g._emitAck += 1;
      const idx = ringT.current.findIndex((t) => t >= 1);
      const i = idx >= 0 ? idx : 0;
      ringT.current[i] = 0;
      if (meshRefs.current[i]) meshRefs.current[i].visible = true;
    }
    // brian auto-emit
    if (g.autoEmit && g.phase === "acoustic") {
      g.emitClock += dt;
      if (g.emitClock > 0.85) {
        g.emitClock = 0;
        g.emitPulse();
      }
    }
    const t = state.clock.elapsedTime;
    for (let i = 0; i < COUNT; i++) {
      const m = meshRefs.current[i];
      if (!m) continue;
      if (ringT.current[i] < 1) {
        ringT.current[i] += dt * 0.85;
        const p = ringT.current[i];
        const r = 0.12 + p * 3.1;
        m.scale.set(r, r, r);
        m.material.opacity = Math.max(0, 1 - p) * 0.85;
        m.lookAt(camera.position);
        if (ringT.current[i] >= 1) m.visible = false;
      }
    }
    if (pulseRef.current) {
      // hide the origin pulse when the camera is right on top of it (Stanley's own first-person view)
      pulseRef.current.visible = camera.position.distanceTo(pulseRef.current.position) > 1.0;
      pulseRef.current.material.opacity = 0.6 + Math.sin(t * 6) * 0.3;
      const ps = 1 + Math.sin(t * 6) * 0.12;
      pulseRef.current.scale.set(ps, ps, ps);
    }
  });

  return (
    <group>
      {new Array(COUNT).fill(0).map((_, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          geometry={ringGeo}
          material={material.clone()}
          position={[S_POS.x, S_POS.y, S_POS.z]}
          visible={false}
        />
      ))}
      <mesh ref={pulseRef} position={[S_POS.x, S_POS.y, S_POS.z]}>
        <sphereGeometry args={[0.16, 20, 20]} />
        <meshBasicMaterial color={RING_COLOR} transparent opacity={0.85} />
      </mesh>
    </group>
  );
}
